import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetGenericAccountByIdMutation } from "../../../api/services/client/clientApis";
import { MuiBox, MuiFooterButtons } from "../../../components";
import { MODULE_PERMISSIONS, PAGE_URLS } from "../../../helpers/constants";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { createGenericAccounts } from "../createGenericAccount/siteCore/SiteCoreKeys";
import { GENERIC_ACCOUNT_TAB_INDEX } from "../editGenericAccountTab/EditGenericAccountTab";
import { ViewGenericAccountWrapper } from "./ViewGenericAccount.style";
import ViewGenericAccountHeader from "./viewGenericAccountHeader/ViewGenericAccountHeader";
import ViewGenericAccountKeyDetails from "./viewGenericAccountKeyDetails/ViewGenericAccountKeyDetails";
import ViewGenericAccountMain from "./viewGenericAccountMain/ViewGenericAccountMain";
import { usePermissions } from "../../../hooks/usePermissions";

const ViewGenericAccount: FC = () => {
  const { commonLabels, fetchLabel } = useSiteCore();
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();

  const location = useLocation();
  const genericAccountId = location?.state?.genericAccountId;
  const tabIndex = location?.state?.tabIndex;

  const [currentStep, setCurrentStep] = useState<number>(
    tabIndex ?? GENERIC_ACCOUNT_TAB_INDEX.GenericAccountDetails,
  );

  const { mutateAsync: getGenericAccountData, data: genericAccountData } =
    useGetGenericAccountByIdMutation();

  useEffect(() => {
    if (genericAccountId) {
      getGenericAccountData(genericAccountId);
    }
  }, [genericAccountId]);

  const handleNavigation = () => {
    navigate(-1);
  };

  const handleEditGenericAccountNavigation = () => {
    navigate(PAGE_URLS.EDIT_GENERIC_ACCOUNTS, {
      state: {
        genericAccountId: genericAccountId,
        tabIndex: currentStep,
      },
    });
  };

  return (
    <ViewGenericAccountWrapper>
      <MuiBox>
        <ViewGenericAccountHeader
          title={fetchLabel(
            createGenericAccounts.createGenericAccountGenericAccountHeaderTitle,
            createGenericAccounts.viewGenericAccountHeading,
          )}
          showEditButton={true}
          onEditClick={handleEditGenericAccountNavigation}
        />
        <ViewGenericAccountKeyDetails
          accountsAssigned={genericAccountData?.numberOfAccounts?.assigned}
          accountsRegistered={genericAccountData?.numberOfAccounts?.registered}
          status={genericAccountData?.status}
          expirationDate={genericAccountData?.expirationDate}
          genericRegistrationCode={genericAccountData?.registrationCode}
        />
        <MuiBox className="separator-space" />

        <ViewGenericAccountMain
          data={genericAccountData}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />

        <MuiBox className="separator-space" />

        <MuiFooterButtons
          secondaryButtonLabel={commonLabels.close}
          primaryButtonLabel={
            hasModulePermission(MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT)
              ? commonLabels.edit
              : ""
          }
          onSecondaryClick={handleNavigation}
          onPrimaryClick={handleEditGenericAccountNavigation}
        />

        <MuiBox className="separator-space" />
      </MuiBox>
    </ViewGenericAccountWrapper>
  );
};

export default ViewGenericAccount;
